/**
 * Thin auth helpers shared by PrivateRoute, LoginPage, UserMenu, CommandPalette.
 *
 * Token lives in `getMcpAccessToken()` (issued by mcp.smplify.com). The
 * legacy admin token storage stays in `apiClient.ts` but the UI no longer
 * mints one — SSO is the only login path.
 */
import {
  clearAuthToken,
  clearMcpAccessToken,
  getAuthToken,
  getMcpAccessToken,
  getTenantId,
  setTenantId,
} from "@/services/apiClient";
import { queryClient } from "@/lib/queryClient";

function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function isAuthenticated(): boolean {
  return Boolean(getMcpAccessToken() || getAuthToken());
}

export function isTokenExpired(token?: string | null): boolean {
  const t = token ?? getMcpAccessToken() ?? getAuthToken();
  if (!t) return true;
  const claims = decodeJwt(t);
  const exp = typeof claims?.exp === "number" ? (claims.exp as number) : undefined;
  if (!exp) return false; // no exp claim → assume valid
  return Date.now() / 1000 >= exp;
}

/** Pull tenant_id out of the bearer JWT and persist it for future requests. */
export function bootstrapTenant(): string | null {
  if (getTenantId()) return getTenantId();
  const token = getMcpAccessToken() ?? getAuthToken();
  if (!token) return null;
  const claims = decodeJwt(token);
  const tenant =
    (claims?.tenant_id as string | undefined) ??
    (claims?.tenantId as string | undefined) ??
    (claims?.tid as string | undefined) ??
    ((claims?.app_metadata as Record<string, unknown> | undefined)?.tenant_id as string | undefined);
  if (tenant) {
    setTenantId(String(tenant));
    return String(tenant);
  }
  return null;
}

export function logout() {
  clearAuthToken();
  clearMcpAccessToken();
  try {
    localStorage.removeItem("smplify_tenant_id");
  } catch { /* noop */ }
  // Drop every cached query so the next session never sees stale tenant data.
  try {
    queryClient.cancelQueries();
    queryClient.clear();
  } catch { /* noop */ }
  window.location.assign("/login");
}

export function getCurrentUserEmail(): string | null {
  const token = getMcpAccessToken() ?? getAuthToken();
  if (!token) return null;
  const claims = decodeJwt(token);
  return (claims?.email as string | undefined) ?? (claims?.sub as string | undefined) ?? null;
}
